import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight, Hash, AlertCircle, Copy, Check } from 'lucide-react';
import { Link } from 'wouter';
import { truncateHash } from '@/lib/utils';
import { fetchLatestTransactions, type TransactionData } from '@/data-access/blocks';

interface LatestTransactionsTableProps {
  className?: string;
}

const defaultTransactions: TransactionData[] = [
  { hash: '16c356489660e2f14b63dabd9b17c98f0ee22b74cc6b55e8a4c59ea8c506d02da3', bitcoinValue: 0.000214, size: 110, fee: 9.1, satFeePerVByte: 82727 },
  { hash: 'facb3d5c9620f8bc77f35a6e5b7c6a4d2e1f4a8c9d7b5e6f3a2b1c4d8e7f9a0b3', bitcoinValue: 0.005595, size: 433, fee: 2.7, satFeePerVByte: 6235 },
  { hash: '18ecc27208409bc42e7b64a7b3d2c8f9e1a5b6c7d4e8f2a9b3c6d5e7f1a4b8c2', bitcoinValue: 0.000383, size: 141, fee: 8.0, satFeePerVByte: 56737 },
  { hash: '8100c1437b70e9f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f6', bitcoinValue: 0.002416, size: 141, fee: 4.0, satFeePerVByte: 28368 },
  { hash: '382f8864696af3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8', bitcoinValue: 0.000583, size: 354, fee: 1.2, satFeePerVByte: 3389 }
];

export function LatestTransactionsTable({ 
  className 
}: LatestTransactionsTableProps) {
  const [copiedHash, setCopiedHash] = useState<string | null>(null);
  
  const { 
    data: transactions, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['latest-transactions'],
    queryFn: fetchLatestTransactions,
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 15000, // Consider data stale after 15 seconds
  });

  const handleCopyHash = (hash: string) => {
    navigator.clipboard.writeText(hash);
    setCopiedHash(hash);
    setTimeout(() => setCopiedHash(null), 2000);
  };

  // Use real data if available, otherwise fall back to default
  const displayTransactions = transactions || defaultTransactions;
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900/20">
            <div className="w-6 h-6 flex items-center justify-center">
              <div className="relative">
                <div className="w-4 h-4 border-2 border-orange-600 dark:border-orange-400 rounded-full"></div>
                <ArrowRight className="w-2 h-2 text-orange-600 dark:text-orange-400 absolute top-1 left-1" />
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-foreground">Latest Transactions</h3>
              {error && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs text-muted-foreground">Cached</span>
                </div>
              )}
              {!isLoading && !error && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-muted-foreground">Live</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">Recent network activity</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wider">
                  Transaction Hash
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wider">
                  Bitcoin Value
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wider text-right">
                  Fee Rate
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeleton rows
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : (
                // Data rows
                displayTransactions.map((tx, index) => (
                  <TableRow key={index} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Hash className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                        <Link 
                          href={`/search/transaction?query=${tx.hash}`} 
                          className="font-mono text-sm text-muted-foreground hover:text-orange-600 dark:hover:text-orange-400 transition-colors cursor-pointer"
                        >
                          {truncateHash(tx.hash)}
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-orange-600 dark:hover:text-orange-400"
                          onClick={() => handleCopyHash(tx.hash)}
                        >
                          {copiedHash === tx.hash ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <span className="font-medium">
                          {tx.bitcoinValue.toFixed(6)}
                        </span>
                        <span className="text-xs text-muted-foreground">BTC</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">{tx.satFeePerVByte.toFixed(2)} sat/vB</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}